/**
 * Valida un email
 * @param email - Email a validar
 * @returns Mensaje de error o null si es valido
 */
export const validateEmail = (email: string): string | null => {
  // Expresion regular simplificada para validar emails
  const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;

  if (!email || !email.trim()) {
    return 'El email es requerido';
  }

  const trimmedEmail = email.trim();

  if (!emailRegex.test(trimmedEmail)) {
    return 'El email no es válido';
  }

  return null;
};

export const validatePassword = (password: string): string | null => {
  const minLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);

  if (!minLength) return 'La contraseña debe tener al menos 8 caracteres';
  if (!hasUpperCase) return 'La contraseña debe tener al menos una mayúscula';
  if (!hasLowerCase) return 'La contraseña debe tener al menos una minúscula';
  if (!hasNumber) return 'La contraseña debe tener al menos un número';
  return null;
};

/**
 * Valida un numero de telefono chileno
 * @param phone - Numero de telefono a validar
 * @returns Mensaje de error o null si es valido
 */
export const validatePhone = (phone: string): string | null => {
  if (!phone) return null;

  const cleanPhone = phone.replace(/[^0-9+]/g, '');

  if (!cleanPhone) return null;

  if (cleanPhone.startsWith('+')) {
    if (cleanPhone.length !== 12) {
      return 'El número con código de país debe tener 12 dígitos (ej: +56912345678)';
    }
    if (!cleanPhone.startsWith('+56')) {
      return 'El código de país debe ser +56';
    }
    if (!/^\+56[89]\d{8}$/.test(cleanPhone)) {
      return 'El número debe comenzar con 8 o 9 después del código de país';
    }
  } else {
    // Numero sin codigo de pais
    if (cleanPhone.length !== 9) {
      return 'El número debe tener 9 dígitos';
    }
    if (!/^[89]\d{8}$/.test(cleanPhone)) {
      return 'El número debe comenzar con 8 o 9';
    }
  }

  return null;
};

export const formatPhoneNumber = (phone: string): string => {
  // Eliminar todo excepto numeros y '+'
  let cleaned = phone.replace(/[^\d+]/g, '');

  // Si esta vacio, retornar vacio
  if (!cleaned) return '';

  // Si tiene exactamente 9 digitos y no tiene codigo de pais, dejarlo asi
  if (/^\d{9}$/.test(cleaned)) {
    return cleaned;
  }

  // Si empieza con numeros y tiene mas de 9 digitos
  if (!cleaned.startsWith('+') && cleaned.length > 9) {
    // Si empieza con 56, agregar el +
    if (cleaned.startsWith('56')) {
      cleaned = '+' + cleaned;
    } else {
      // Si no empieza con 56, agregar +56
      cleaned = '+56' + cleaned;
    }
  }

  // Limitar la longitud total
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.slice(0, 12); // +56 + 9 digitos
  } else {
    cleaned = cleaned.slice(0, 9); // solo 9 digitos
  }

  return cleaned;
};

/**
 * Valida un RUT chileno
 * @param rut - RUT a validar (formato: 12345678-9)
 * @returns Mensaje de error o null si es valido
 */
export const validateRut = (rut: string): string | null => {
  if (!rut) return null;

  // Validar formato basico (puede incluir puntos y guion)
  if (!/^[\d.]+-[0-9kK]$/.test(rut)) {
    return 'El formato del RUT no es válido';
  }

  // Limpiar el RUT de puntos y guiones
  let valor = rut.replace(/\./g, '').replace('-', '');

  // Obtener digito verificador
  const dv = valor.slice(-1).toLowerCase();
  // Obtener cuerpo del RUT
  const rutNumerico = parseInt(valor.slice(0, -1), 10);

  // Calcular digito verificador
  let suma = 0;
  let multiplicador = 2;
  let rutTemp = rutNumerico;

  while (rutTemp > 0) {
    suma += (rutTemp % 10) * multiplicador;
    rutTemp = Math.floor(rutTemp / 10);
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }

  const dvCalculado = 11 - (suma % 11);
  const dvEsperado =
    dvCalculado === 11
      ? '0'
      : dvCalculado === 10
        ? 'k'
        : dvCalculado.toString();

  if (dv !== dvEsperado) {
    return 'El RUT no es válido';
  }

  return null;
};

export const validateDateOfBirth = (date: string): string | null => {
  if (!date) return null;

  const birthDate = new Date(date);
  const today = new Date();

  // Verificar que la fecha sea valida
  if (isNaN(birthDate.getTime())) {
    return 'La fecha de nacimiento no es válida';
  }

  // Verificar que la fecha no sea futura
  if (birthDate > today) {
    return 'La fecha de nacimiento no puede ser futura';
  }

  // Verificar que la persona sea mayor de edad (18 anos)
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (
    age < 18 ||
    (age === 18 && (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)))
  ) {
    return 'Debe ser mayor de 18 años';
  }

  return null;
};
